import React, { useEffect, useState } from 'react';
import {
    Slider,
    Alert,
    TextArea,
    Button,
} from '@kaleyra/kds';
import Styles from "../../Security.module.scss";
import { useTranslation } from 'react-i18next';
import { range } from 'lodash';
import { checkIfValidIPV6, checkWhiteListedApiKeyLimit, validateIpAddress } from '../../../../../utils';
import infoIcon from '../../../../../assets/infoIconBlack.svg';
import { Spinner } from '@kaleyra/emmy';
import KDSToast from '../../../../../components/KDSToast';

export type MapStatePropsType = {
    sidebarVisibility: boolean;
    params: any;
};

export type DispatchPropsType = {
    onClose: () => void;
    onSave: (data: any, callBack: any) => void;
    onIpRestrictionUpdate: (data: any, callBack: any) => void;
    onLoad: () => void;
};

const AddEditIpConfig: React.FC<MapStatePropsType & DispatchPropsType> = ({
    onClose,
    onLoad,
    onSave,
    onIpRestrictionUpdate,
    params,
    sidebarVisibility
}) => {

    const { t } = useTranslation();
    const [ipAddresses, setIpAddresses] = useState('');
    const [currentIpAddresses, setCurrentIpAddresses] = useState('');
    const [ipAddressesErr, setIpAddressesErr] = useState('');
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);

    const allowedIpRangeIpv4 = range(8, 31);
    const allowedIpRangeIpv6 = range(1, 129);

    useEffect(() => {
        const allowedIp = params?.data?.allowed_ips;
        const current_ip = params?.data?.current_ip;

        if (allowedIp) {
            setIpAddresses(allowedIp);
            setCurrentIpAddresses(current_ip);
        } else {
            setIpAddresses(current_ip);
            setCurrentIpAddresses(current_ip);
        }
    }, []);

    const handleOnSave = () => {
        setSaving(true);
        if ((params?.data?.allowed_ips || editing) && params?.data?.status != 9) {
            onIpRestrictionUpdate({ allowed_ips: ipAddresses,status:1, activity_log_action: 'update', activity_log_object: 'IP RESTRICTION', activity_log_value: { allowed_ips: ipAddresses } }, (resp: any, success: boolean) => {
                if (success) {
                    KDSToast(t("requestExecuted"), 'success')
                    onLoad();
                    onClose();
                } else {
                    KDSToast(t("KLR02515"), 'error')
                    setSaving(false);
                }
            })
        }
        else {
            onSave(({ allowed_ips: ipAddresses, activity_log_action: 'add', activity_log_object: 'IP RESTRICTION', activity_log_value: { allowed_ips: ipAddresses } }), (resp: any, success: boolean) => {
                if (success) {
                    setEditing(true);
                    KDSToast(t("requestExecuted"), 'success')
                    onLoad();
                    onClose();
                } else {
                    KDSToast(t("KLR02515"), 'error')
                    setSaving(false);
                }
            })
        }
    }

    const validateCheck = (field: string) => {

        if (field === 'ipAddresses') {

            let res = ipAddresses.split(',');
            const duplicateIps = res.filter((item: any, index: number) => res.indexOf(item) !== index);

            if (!String(res || ipAddresses.length <= 0)) {
                return t('titleReq');
            }
            if(!res.includes(currentIpAddresses)){
                return 'Current IP must be added to the allowed list'
            }
            if (res.length > checkWhiteListedApiKeyLimit()) {
                return t("KLR02668", { "maxIpCount": checkWhiteListedApiKeyLimit() });
            }
            if (duplicateIps.length > 0) {
                return t("KLR02669");
            }
            for (let i = 0; i < res.length; i++) {
                let ip = res[i].split('/');
                if (res[i].includes('/')) {

                    if (!validateIpAddress((ip)[0])
                        || ip.includes('')
                        || (checkIfValidIPV6((ip)[0]) ? !allowedIpRangeIpv6.includes(Number(ip[1])) : !allowedIpRangeIpv4.includes(Number(ip[1])))
                    ) {
                        return t('apiValidIp');
                    }
                }
                else {
                    if (!validateIpAddress((ip)[0])) {
                        return t('apiValidIp');
                    }
                }
            }
        }
        return '';
    };

    const validateFields = (field: string): 'error' | undefined => {
        if (validateCheck(field)) {
            return "error";
        };
        return undefined;
    };

    const setErrors = (field: string) => {
        let errMsg = validateCheck(field);
        if (errMsg) {
            return (
                <div className={Styles.errorClass}>
                    {errMsg}
                </div>
            );
        };
    };

    const handleIpAddressesOnBlur = () => {
        let errMsg = validateCheck('ipAddresses');
        if (errMsg) {
            setIpAddressesErr(errMsg);
            return;
        } else {
            setIpAddressesErr('');
            return;
        }
    };

    const checkSaveButtonEnabled = () => {
        if (saving || validateFields("ipAddresses")) {
            return true;
        };
    };

    const getFooter = () => {

        return (
            <div className={Styles.actionItems}>
                <div className={Styles.cancelBtnWrapper}>
                    <Button type="default" className={Styles.cancelBtn} onClick={()=>{onClose();onLoad()}}>
                        {t('mainCancel')}
                    </Button>
                </div>
                <div className={Styles.saveBtnWrapper}>
                    <Button
                        className={Styles.saveBtn}
                        type="primary"
                        onClick={handleOnSave}
                        disabled={checkSaveButtonEnabled()}
                    >
                        {saving ? <Spinner margin={"0.5rem"} color='#636C75' /> : t('mainSave')}
                    </Button>
                </div>
            </div>
        )
    };

    const getContent = () => {

        return (
            <>
                <Alert
                    showIcon
                    title={`${'Your IP Address :'} ${currentIpAddresses}`}
                    type='neutral'
                >
                    {t("KLR02853")}
                </Alert>
                <TextArea
                    placeholder={t("KLR02753")}
                    className={Styles.mT16}
                    statusType={ipAddressesErr ? validateFields('ipAddresses') : undefined}
                    statusContent={ipAddressesErr ? setErrors('ipAddresses') : null}
                    label={t("KLR02753")}
                    onChange={(e: any) => {
                        setIpAddresses(e.target.value.replace(/\s/g, ''));
                        setIpAddressesErr('');
                    }}
                    onBlur={() => {
                        handleIpAddressesOnBlur();
                    }}
                    value={ipAddresses}
                />
                <div className={Styles.dF}>
                    <img src={infoIcon} alt='info' className={Styles.infoIcon} />
                    <div className={Styles.ipAddressesTextArea}>{t("KLR02754")}</div>
                </div>
            </>
        )
    };

    return (
        <Slider
            containerId="root-modal"
            onClose={() => { onClose(); onLoad() }}
            visibility={sidebarVisibility}
            footer={getFooter()}
            label='IP Restriction'
        >
            {getContent()}
        </Slider>
    );
};

export default AddEditIpConfig;
